import type { SessionUser } from '@bssb/contracts';
import { setAccessToken } from '@/offline/api-client';
import { isUnlocked, lock as lockDataKey, openField, sealField } from '@/offline/crypto';
import { db, META_KEYS, readMeta, wipeLocalData, writeMeta } from '@/offline/db';
import { resetSearchKey } from '@/offline/search-tokens';

/**
 * Sesi lapangan (T-P7-06, FR-AUTH-04/08/09, §6.2).
 *
 * Tiga hal dipisahkan dengan sengaja:
 *
 * - **Access token** hidup di memori saja (`api-client`). Tab yang ditutup
 *   kehilangannya, dan itu memang yang diinginkan.
 * - **Profil pengguna** disimpan sebagai ciphertext, karena nama dan cabang
 *   collector adalah data pribadi yang tidak perlu terbaca dari devtools.
 * - **Amplop sesi** (username, batas grant offline, waktu sync terakhir) disimpan
 *   terbuka: layar kunci harus tahu siapa yang mau membuka dan apakah grant-nya
 *   masih berlaku *sebelum* kunci terbuka. Tanpa itu tidak ada cara membedakan
 *   "belum pernah login" dari "terkunci".
 */

/** FR-AUTH-04: sesi lapangan terkunci setelah 12 jam tanpa aktivitas. */
export const IDLE_LOCK_MS = 12 * 60 * 60 * 1000;

/** FR-SYNC-11: perangkat tanpa sinkronisasi lebih dari 7 hari menghapus cache. */
export const STALE_WIPE_MS = 7 * 24 * 60 * 60 * 1000;

export interface SessionEnvelope {
  username: string;
  /** Profil `SessionUser` terenkripsi (lihat `crypto.ts`). */
  profileSealed: string;
  branchName: string | null;
  deviceId: string | null;
  /** Batas pemakaian offline; diperbarui tiap kali login/refresh online (§6.2). */
  offlineGrantExpiresAt: string;
  lastOnlineAuthAt: string;
  lastActiveAt: string;
  /** Sudah pernah mengunduh dataset awal — menentukan onboarding vs beranda. */
  bootstrapped: boolean;
  biometricEnabled: boolean;
}

export type AuthStatus =
  | 'loading'
  /** Belum ada sesi di perangkat ini. */
  | 'anonymous'
  /** Ada sesi, cache masih terkunci — perlu PIN atau biometrik. */
  | 'locked'
  /** Grant offline habis atau cache basi; wajib login online lagi. */
  | 'expired'
  | 'authenticated';

interface SessionState {
  status: AuthStatus;
  envelope: SessionEnvelope | null;
  user: SessionUser | null;
}

let state: SessionState = { status: 'loading', envelope: null, user: null };
const listeners = new Set<(state: SessionState) => void>();

export function getSessionState(): SessionState {
  return state;
}

export function subscribeSessionState(listener: (state: SessionState) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function emit(next: Partial<SessionState>): void {
  state = { ...state, ...next };
  for (const listener of listeners) listener(state);
}

export async function readEnvelope(): Promise<SessionEnvelope | null> {
  return (await readMeta<SessionEnvelope>(META_KEYS.session)) ?? null;
}

async function saveEnvelope(envelope: SessionEnvelope): Promise<void> {
  await writeMeta(META_KEYS.session, envelope);
  emit({ envelope });
}

export async function patchEnvelope(patch: Partial<SessionEnvelope>): Promise<void> {
  const current = await readEnvelope();
  if (!current) return;
  await saveEnvelope({ ...current, ...patch });
}

/** Grant offline habis → aplikasi hanya boleh dibuka lewat login online. */
export function grantExpired(envelope: SessionEnvelope, now = Date.now()): boolean {
  return now > Date.parse(envelope.offlineGrantExpiresAt);
}

export function idleExpired(envelope: SessionEnvelope, now = Date.now()): boolean {
  return now - Date.parse(envelope.lastActiveAt) > IDLE_LOCK_MS;
}

export function staleForWipe(envelope: SessionEnvelope, now = Date.now()): boolean {
  return now - Date.parse(envelope.lastOnlineAuthAt) > STALE_WIPE_MS;
}

export function grantRemainingMs(envelope: SessionEnvelope, now = Date.now()): number {
  return Math.max(Date.parse(envelope.offlineGrantExpiresAt) - now, 0);
}

// --- transisi ------------------------------------------------------------------

export interface OnlineAuthResult {
  user: SessionUser;
  accessToken: string;
  offlineGrantExpiresAt: string;
  deviceId?: string | null;
  branchName?: string | null;
}

/**
 * Catat hasil autentikasi online.
 *
 * Memerlukan data key sudah terbuka: profil disimpan terenkripsi, dan mengizinkan
 * jalur yang menyimpannya terbuka "sementara" adalah cara paling mudah
 * kehilangan sifat itu tanpa disadari.
 */
export async function completeOnlineAuth(result: OnlineAuthResult): Promise<void> {
  if (!isUnlocked()) throw new Error('Kunci lokal belum terbuka');

  const now = new Date().toISOString();
  const existing = await readEnvelope();

  setAccessToken(result.accessToken);

  await saveEnvelope({
    username: result.user.username,
    profileSealed: await sealField(JSON.stringify(result.user)),
    branchName: result.branchName ?? existing?.branchName ?? null,
    deviceId: result.deviceId ?? existing?.deviceId ?? null,
    offlineGrantExpiresAt: result.offlineGrantExpiresAt,
    lastOnlineAuthAt: now,
    lastActiveAt: now,
    bootstrapped: existing?.bootstrapped ?? false,
    biometricEnabled: existing?.biometricEnabled ?? false,
  });

  emit({ status: 'authenticated', user: result.user });
}

/** Buka sesi yang sudah ada setelah PIN/biometrik diterima. */
export async function resumeUnlockedSession(): Promise<SessionUser | null> {
  const envelope = await readEnvelope();
  if (!envelope) {
    emit({ status: 'anonymous', envelope: null, user: null });
    return null;
  }

  if (staleForWipe(envelope)) {
    // Perangkat sudah lebih dari 7 hari tanpa kabar dari server; isinya tidak
    // boleh dipercaya lagi (FR-SYNC-11).
    await hardWipe();
    return null;
  }

  if (grantExpired(envelope)) {
    emit({ status: 'expired', envelope, user: null });
    return null;
  }

  const user = JSON.parse(await openField(envelope.profileSealed)) as SessionUser;
  await saveEnvelope({ ...envelope, lastActiveAt: new Date().toISOString() });
  emit({ status: 'authenticated', user });
  return user;
}

/** Tentukan status awal aplikasi tanpa membuka kunci apa pun. */
export async function bootstrapSession(): Promise<AuthStatus> {
  const envelope = await readEnvelope();

  if (!envelope) {
    emit({ status: 'anonymous', envelope: null, user: null });
    return 'anonymous';
  }

  if (staleForWipe(envelope)) {
    await hardWipe();
    return 'anonymous';
  }

  if (grantExpired(envelope)) {
    emit({ status: 'expired', envelope, user: null });
    return 'expired';
  }

  emit({ status: 'locked', envelope, user: null });
  return 'locked';
}

export function lockSession(): void {
  lockDataKey();
  setAccessToken(null);
  emit({ status: state.envelope ? 'locked' : 'anonymous', user: null });
}

export async function touchActivity(): Promise<void> {
  const envelope = await readEnvelope();
  if (!envelope) return;

  // Tulis paling banyak sekali per menit: setiap ketukan layar memanggil ini,
  // dan menulis IndexedDB sesering itu membebani perangkat lapangan.
  if (Date.now() - Date.parse(envelope.lastActiveAt) < 60_000) return;
  await saveEnvelope({ ...envelope, lastActiveAt: new Date().toISOString() });
}

/** Logout & remote wipe: cache nasabah lenyap bersama sesinya (FR-AUTH-09/10). */
export async function hardWipe(): Promise<void> {
  setAccessToken(null);
  lockDataKey();
  resetSearchKey();
  await wipeLocalData();
  emit({ status: 'anonymous', envelope: null, user: null });
}

/** Ringkasan untuk halaman Akun; aman dipanggil dalam keadaan terkunci. */
export async function sessionSummary(): Promise<{
  lastSyncAt: string | null;
  pendingOutbox: number;
  pendingMedia: number;
}> {
  return {
    lastSyncAt: (await readMeta<string>(META_KEYS.lastSyncAt)) ?? null,
    pendingOutbox: await db.outbox.where('status').noneOf(['SENT']).count(),
    pendingMedia: await db.mediaQueue.where('status').anyOf('PENDING', 'UPLOADING').count(),
  };
}
